import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useGetDeveloperApiTokens } from "api/queries/user/useGetDeveloperApiTokens";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import Alert from "components/v2/Alert/Alert";
import Button from "components/v2/Button/Button";
import DataTable from "components/v2/Table/DataTable";
import Tablefooter from "components/v2/Tablefooter/Tablefooter";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";

import getDeveloperApiTokenColumns from "./DeveloperApiTokenColumns";
import EmptyDeveloperApiTokensState from "./EmptyDeveloperApiTokensState";
import GenerateTokenModal from "./GenerateTokenModal";
import RevokeDeveloperApiTokenModal from "./RevokeDeveloperApiTokenModal";
import "./DeveloperApiTokens.scss";

interface DeveloperApiToken {
  id: number;
  name: string;
  key_suffix: string;
  created: string;
  created_by: string;
  revoked_at: string | null;
}

export default function DeveloperApiTokens() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const developerSettings = permissions ? JSON.parse(permissions).permissions?.developer : null;

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<DeveloperApiToken | null>(null);

  useEffect(() => {
    if (permissions && !developerSettings?.create_developer_tokens) {
      navigate("/settings/your-details");
    }
  }, [developerSettings, permissions, navigate]);

  const handleRevoke = useCallback((token: DeveloperApiToken) => {
    setTokenToRevoke(token);
  }, []);

  const columns = useMemo(
    () =>
      getDeveloperApiTokenColumns({
        onRevoke: handleRevoke,
      }),
    [handleRevoke],
  );

  const {
    instance,
    loading,
    fetching,
    isError,
    totalPages,
    totalData,
    currentPage,
    itemsPerPage,
    setItemsPerPage,
    setCurrentPage,
  } = useTableQueryRequest(useGetDeveloperApiTokens, columns, {
    useDefaultSorting: false,
  });

  const hasTokens = totalData > 0;

  return (
    <>
      <h3 className="mt-3xl">
        <FormattedMessage id="settings.developerApiTokens.title" defaultMessage="API Tokens" />
      </h3>
      <div className="mt-xs text-tertiary text-md font-regular mb-4xl">
        <FormattedMessage
          id="settings.developerApiTokens.subtitle"
          defaultMessage="Generate and manage API tokens for integrating with the Cargobase API"
        />
      </div>

      {isError ? (
        <Alert variant="error" label={intl.formatMessage({ id: "general.error", defaultMessage: "Error" })}>
          {intl.formatMessage({
            id: "settings.developerApiTokens.loadError",
            defaultMessage: "Failed to load API tokens. Please refresh the page and try again.",
          })}
        </Alert>
      ) : !loading && !fetching && !hasTokens ? (
        <EmptyDeveloperApiTokensState onClickGenerate={() => setShowGenerateModal(true)} />
      ) : (
        <div className="developer-api-tokens-table-container">
          <div className="d-flex justify-content-between gap-lg align-items-center py-xl px-3xl bg-primary">
            <div className="text-md font-medium text-primary">
              <FormattedMessage id="settings.developerApiTokens.tokensHistory" defaultMessage="Tokens history" />
            </div>
            <Button
              label={intl.formatMessage({
                id: "settings.developerApiTokens.generateToken",
                defaultMessage: "Generate token",
              })}
              iconLeft={<PlusIcon />}
              size="sm"
              onClick={() => setShowGenerateModal(true)}
            />
          </div>

          <DataTable
            tableInstance={instance}
            tableLayout="fixed"
            isLoading={loading || fetching}
            getRowClassName={(row) => (row.original.revoked_at ? "opacity-50" : "")}
          />

          <Tablefooter
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalPages={totalPages}
            totalData={totalData}
            currentPage={currentPage ?? 1}
            pageClicked={setCurrentPage}
          />
        </div>
      )}

      <GenerateTokenModal open={showGenerateModal} onClose={() => setShowGenerateModal(false)} />

      {tokenToRevoke && (
        <RevokeDeveloperApiTokenModal
          token={tokenToRevoke}
          open={!!tokenToRevoke}
          onClose={() => setTokenToRevoke(null)}
        />
      )}
    </>
  );
}
